import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import "./components/ScrollFX.css";
import "./styles/global-polish.css";
import ScrollToTop from "./components/ScrollToTop";
import { UIProvider } from "./context/UIContext";

import FloatingContact from "./components/FloatingContact/FloatingContact";

// ================= LAYOUT =================
import ClientLayout from "./layout/ClientLayout";
import AdminLayout from "./admin/layout/AdminLayout";

// ================= USER PAGES (lazy-loaded) =================
const Home = lazy(() => import("./pages/user/Home"));
const About = lazy(() => import("./pages/user/About"));
const Categories = lazy(() => import("./pages/user/Categories"));
const CategoriesProducts = lazy(() => import("./pages/user/CategoriesProducts"));
const ProductDetail = lazy(() => import("./pages/user/ProductDetail"));
const Gallery = lazy(() => import("./pages/user/Gallery"));
const Blogs = lazy(() => import("./pages/user/Blogs"));
const BlogDetails = lazy(() => import("./pages/user/BlogDetails"));
const Contact = lazy(() => import("./pages/user/Contact"));
const Enquiry = lazy(() => import("./pages/user/Enquiry"));
const Certificates = lazy(() => import("./pages/user/Certificates"));
const NotFound = lazy(() => import("./pages/user/NotFound"));

// ================= ADMIN PAGES (lazy-loaded — keeps the heavy
// rich-text editor bundles out of the public site's initial load) =================
const Login = lazy(() => import("./pages/admin/Login"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));

const ViewProducts = lazy(() => import("./pages/admin/Products/ViewProducts"));
const AddProduct = lazy(() => import("./pages/admin/Products/AddProduct"));

const AddCategory = lazy(() => import("./pages/admin/Category/AddCategory"));
const ViewCategories = lazy(() => import("./pages/admin/Category/ViewCategories"));

const AddProject = lazy(() => import("./pages/admin/Projects/AddProject"));
const ViewProjects = lazy(() => import("./pages/admin/Projects/ViewProjects"));

const AddGallery = lazy(() => import("./pages/admin/Gallery/AddGallery"));
const ViewGallery = lazy(() => import("./pages/admin/Gallery/ViewGallery"));

const AddBlog = lazy(() => import("./pages/admin/Blog/AddBlog"));
const ViewBlogs = lazy(() => import("./pages/admin/Blog/ViewBlogs"));

const Enquiries = lazy(() => import("./pages/admin/Enquiries"));

// Lightweight full-page loader shown briefly while a lazy page chunk loads
function PageLoader() {
  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div className="app-page-loader-spinner" />
    </div>
  );
}

function App() {
  return (
    <UIProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>

          {/* ================= USER ROUTES ================= */}

          <Route path="/" element={<ClientLayout />}>
            <Route index element={<Home />} />
            <Route path="about" element={<About />} />

            {/* Category listing + category ke products */}
            <Route path="categories" element={<Categories />} />
            <Route path="categories/:slug" element={<CategoriesProducts />} />

            {/* Single product detail */}
            <Route path="product/:slug" element={<ProductDetail />} />

            <Route path="gallery" element={<Gallery />} />
            <Route path="blogs" element={<Blogs />} />
            <Route path="/blogs/:slug" element={<BlogDetails />} />
            <Route path="contact" element={<Contact />} />
            <Route path="enquiry" element={<Enquiry />} />
            <Route path="certificates" element={<Certificates />} />
          </Route>

          {/* ================= ADMIN LOGIN ================= */}

          <Route path="/admin/login" element={<Login />} />

          {/* ================= ADMIN ROUTES ================= */}

          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Dashboard />} />

            {/* Products */}
            <Route path="products/add" element={<AddProduct mode="add" />} />
            <Route path="products/view" element={<ViewProducts />} />
            <Route path="products/edit/:id" element={<AddProduct mode="edit" />} />

            {/* Categories */}
            <Route path="categories/add" element={<AddCategory mode="add" />} />
            <Route path="categories/view" element={<ViewCategories />} />
            <Route path="categories/edit/:id" element={<AddCategory mode="edit" />} />

            {/* Projects */}
            <Route path="projects/add" element={<AddProject />} />
            <Route path="projects/view" element={<ViewProjects />} />
            <Route path="projects/edit/:id" element={<AddProject mode="edit" />} />

            {/* Gallery */}
            <Route path="gallery/add" element={<AddGallery />} />
            <Route path="gallery/view" element={<ViewGallery />} />

            {/* Blog */}
            <Route path="/admin/blog/add" element={<AddBlog mode="add" />} />
            <Route path="/admin/blog/edit/:id" element={<AddBlog mode="edit" />} />
            <Route path="/admin/blog/view" element={<ViewBlogs />} />

            {/* Enquiries */}
            <Route path="enquiries" element={<Enquiries />} />
          </Route>

          {/* ================= 404 ================= */}

          <Route path="*" element={<NotFound />} />

        </Routes>
        </Suspense>

        {/* Shows on every route — Home, About, Products, Contact, Admin, etc. */}
        <FloatingContact />

      </BrowserRouter>
    </UIProvider>
  );
}

export default App;